import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Activity, AlertCircle, ChevronDown, ChevronRight, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function Section({ title, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 rounded-md bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-zinc-50"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 text-zinc-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-zinc-500" />
          )}
          <span className="text-sm font-semibold text-zinc-900">{title}</span>
        </div>
        <span className="text-xs font-mono-num text-zinc-500">
          {count}
        </span>
      </button>
      {open && <div className="px-4 pb-3 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

function JsonBlock({ data }) {
  return (
    <pre className="mt-2 text-2xs font-mono-num bg-zinc-950 text-zinc-100 p-3 rounded overflow-auto max-h-96">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

export default function CustomerTrace({ email, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !email) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setData(null);
    api
      .get(`/customer/trace`, { params: { email }, timeout: 180000 })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [email, open]);

  const summary = data?.summary;
  const ep = data?.endpoints || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="customer-trace-dialog"
        className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Activity className="h-5 w-5" /> Acuity diagnostic: {email}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-600">
            Probes every Acuity endpoint we have access to and shows raw results.
            Use this to figure out where recurring/subscription payments are actually stored.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 thin-scroll mt-2 space-y-3">
          {loading && (
            <div className="py-12 text-center text-sm text-zinc-500">
              Scanning Acuity… this can take ~30s for orders+certificates+appointments+payments.
            </div>
          )}

          {err && (
            <div className="border border-red-200 bg-red-50 rounded p-3 text-sm text-red-900 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold">Diagnostic failed</div>
                <div className="font-mono-num text-xs mt-1">{err}</div>
              </div>
            </div>
          )}

          {!loading && summary && (
            <>
              <div className="border border-zinc-200 rounded-md bg-zinc-50 p-3">
                <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Summary
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-zinc-500">Clients</div>
                    <div className="font-mono-num font-semibold">{summary.clients_matched}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Orders</div>
                    <div className="font-mono-num font-semibold">{summary.orders_matched}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Certificates</div>
                    <div className="font-mono-num font-semibold">{summary.certificates_matched}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Appointments</div>
                    <div className="font-mono-num font-semibold">{summary.appointments_matched}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Appt Payments</div>
                    <div className="font-mono-num font-semibold">{summary.appointment_payments_total}</div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 h-8 border-zinc-300"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                    toast.success("Diagnostic JSON copied to clipboard");
                  }}
                  data-testid="customer-trace-copy-btn"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy full JSON
                </Button>
              </div>

              <Section
                title="Clients (/clients?search=)"
                count={`${summary.clients_matched} matched`}
                defaultOpen={summary.clients_matched > 0 && summary.clients_matched <= 3}
              >
                <JsonBlock data={ep.clients} />
              </Section>

              <Section
                title="Orders (/orders full-scan)"
                count={`${summary.orders_matched} matched of ${ep.orders?.scanned ?? 0} scanned`}
                defaultOpen={true}
              >
                <JsonBlock data={ep.orders} />
              </Section>

              <Section
                title="Certificates (/certificates)"
                count={`${summary.certificates_matched} matched of ${ep.certificates?.total_in_account ?? 0}`}
                defaultOpen={summary.certificates_matched > 0}
              >
                <JsonBlock data={ep.certificates} />
              </Section>

              <Section
                title="Appointments (/appointments)"
                count={`${summary.appointments_matched} matched`}
              >
                <JsonBlock data={ep.appointments} />
              </Section>

              <Section
                title="Appointment Payments (/appointments/:id/payments)"
                count={`${summary.appointment_payments_total} payments`}
                defaultOpen={true}
              >
                <JsonBlock data={ep.appointment_payments} />
              </Section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
